import React, { useState, useEffect, useRef } from 'react';
import HealthBubble from './HealthBubble';
import BubbleText from './BubbleText';
import moment from 'moment';
import { getFaseBox } from '../../faseMonitor/service';
import { updateTheme } from '../../faseMonitor/actions';

const utils = require('@airfi-disco/shared/utils');

const { isFaseFirmwareOk, isFaseOnlineOk, isFaseContentOk } = utils;

declare const discoStaticPath: string;

interface BoxFieldProps {
  label: string;
  children?: React.ReactNode;
}

function BoxField({ label, children }: BoxFieldProps) {
  return (
    <div className="fase-monitor__box-field">
      <div className="fase-monitor__box-field-label">{label}</div>
      <div className="fase-monitor__box-field-value">{children}</div>
    </div>
  );
}

interface BoxPageProps {
  serial?: string;
}

function BoxPage({ serial = '' }: BoxPageProps) {
  const loadGenerationRef = useRef(0);
  const [box, setBox] = useState(null as any);
  const [loadError, setLoadError] = useState(null as string | null);

  useEffect(() => {
    let isCancelled = false;
    const loadId = ++loadGenerationRef.current;
    setBox(null);
    setLoadError(null);

    async function loadBox() {
      try {
        const resultBox = await getFaseBox(serial);
        if (isCancelled || loadId !== loadGenerationRef.current) {
          return;
        }
        setBox(resultBox);

        const customerCode = resultBox.currentCustomer && resultBox.currentCustomer.customerCode;
        if (customerCode) {
          updateTheme(customerCode, loadId);
        }
      } catch (error: any) {
        if (isCancelled || loadId !== loadGenerationRef.current) {
          return;
        }
        setLoadError(error && error.message ? error.message : String(error));
      }
    }

    loadBox();

    return () => {
      isCancelled = true;
    };
  }, [serial]);

  if (loadError) {
    return (
      <div className="fase-monitor__status fase-monitor__status--error">Something went wrong. Please try again.</div>
    );
  }

  if (!box) {
    return <div className="fase-monitor__status">Loading box details…</div>;
  }

  const {
    currentFirmware = { version: '' },
    assignedFirmware = { version: '' },
    pendingContentCount,
    health,
    lastSeenTimestamp,
    aircraft,
  } = box;
  const tailNumber = aircraft && aircraft.tailNumber ? aircraft.tailNumber : '—';
  document.title = serial;
  const link = (document.querySelector("link[rel*='icon']") || document.createElement('link')) as HTMLLinkElement;
  link.type = 'image/png';
  link.rel = 'shortcut icon';
  link.href = `${discoStaticPath}/static/images/${health}.png`;
  document.getElementsByTagName('head')[0].appendChild(link);
  const lastUpdateFromNow = lastSeenTimestamp ? moment(lastSeenTimestamp).fromNow() : 'No data';
  const firmwareOk = isFaseFirmwareOk(box);
  const firmwareText = `Version: ${firmwareOk ? currentFirmware.version : `${currentFirmware.version} (should be ${assignedFirmware.version})`}`;

  return (
    <div className="fase-monitor__box-page">
      <div className="fase-monitor__box-hero">
        <HealthBubble type={health} width={20} />
        <h1 className="fase-monitor__box-title">{`FASE Box Details - ${serial || ''}`}</h1>
        <img className="fase-monitor__box-image" src={`${discoStaticPath}/static/images/venus-box-128.png`} alt="" />
      </div>

      <div className="fase-monitor__box-details">
        <BoxField label="AIRCRAFT">{tailNumber}</BoxField>
        <BoxField label="FIRMWARE">
          {currentFirmware.version && <BubbleText text={firmwareText} bubbleType={firmwareOk ? 'ok' : 'halting'} />}
          {!currentFirmware.version && <BubbleText text="Version is unknown" bubbleType="failing" />}
        </BoxField>
        <BoxField label="LAST SEEN">
          <BubbleText
            tooltip={lastSeenTimestamp || 'no data'}
            text={lastUpdateFromNow}
            bubbleType={isFaseOnlineOk(lastSeenTimestamp) ? 'ok' : 'failing'}
          />
        </BoxField>
        <BoxField label="CONTENT">
          <BubbleText
            text={`Pending: ${pendingContentCount}`}
            bubbleType={isFaseContentOk(pendingContentCount) ? 'ok' : 'halting'}
          />
        </BoxField>
      </div>
    </div>
  );
}

export default BoxPage;
